import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { WhySolar } from './pages/WhySolar';
import { OurTeam } from './pages/OurTeam';
import { AboutUs } from './pages/AboutUs';
import { HowItWorks } from './pages/HowItWorks';
import { KeyBenefits } from './pages/KeyBenefits';
import { ProjectForm } from './pages/ProjectForm';
import { Dashboard } from './pages/Dashboard';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { HomeHeader } from './components/HomeHeader';
import { Footer } from './components/Footer';
import { useAuthStore } from './store/authStore';
import { Sun, Battery, Zap, LineChart, Globe2, Leaf, Shield, Award, Loader2 } from 'lucide-react';
import { LoginModal } from './components/LoginModal';
import { BrandLogo } from './components/BrandLogo';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';

// Dashboard Button Component
function DashboardButton({ className, children, setShowLoginModal }: {
  className: string;  
  children: React.ReactNode;
  setShowLoginModal: (show: boolean) => void;
}) {
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setShowLoginModal(true);
    }
  };

  if (isLoading) {
    return (
      <button className={className} disabled>
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        Loading...
      </button>
    );
  }

  return (
    <button onClick={handleClick} className={className}>
      {children}
    </button>
  );
}

function App() {
  const { user, isLoading } = useAuthStore();
  const [showLoginModal, setShowLoginModal] = React.useState(false);

  return (
    <Router>
      <Routes>
        <Route path="/why-solar" element={<WhySolar />} />
        <Route path="/our-team" element={<OurTeam />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/key-benefits" element={<KeyBenefits />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/project/new" element={<ProjectForm />} />
        <Route path="/new-project" element={<ProjectForm />} />
        <Route path="/project/:id" element={<ProjectForm />} />
        <Route path="/project/:id/details" element={<ProjectForm />} />
        <Route path="/subscription" element={
          user ? <SubscriptionPage /> : <Navigate to="/" />
        } />
        <Route 
          path="/dashboard" 
          element={
            user ? (
              <Dashboard />
            ) : (
              <Navigate to="/" />
            )
          } 
        />
        <Route path="/" element={
          <div className="min-h-screen bg-white">
            <HomeHeader />
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50 opacity-70" />
              <div className="absolute inset-0" style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?auto=format&fit=crop&w=2000&q=80')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.1
              }} />

              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
                <div className="text-center mb-16">
                  <div className="flex justify-center mb-12">
                    <div className="bg-white p-6 rounded-2xl shadow-xl backdrop-blur-lg bg-opacity-90 transform hover:scale-105 transition-transform duration-300">
                      <div className="flex items-center">
                        <BrandLogo className="h-14 w-auto" />
                      </div>
                    </div>
                  </div>

                  <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                    Intelligent Energy
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-green-600">
                      {" "}Design Solutions
                    </span>
                  </h1>

                  <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-12 leading-relaxed">
                    Transform your energy consumption with our advanced AI-powered design system. 
                    Solar, hybrid, and battery solutions tailored to your needs.
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mb-16">
                    <div className="bg-white rounded-2xl p-6 shadow-lg backdrop-blur-lg bg-opacity-90 transform hover:scale-105 transition-transform duration-300">
                      <Sun className="h-8 w-8 text-yellow-500 mx-auto mb-4" />
                      <div className="text-3xl font-bold text-gray-900 mb-2">95%</div>
                      <div className="text-gray-600">Energy Savings</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg backdrop-blur-lg bg-opacity-90 transform hover:scale-105 transition-transform duration-300">
                      <Leaf className="h-8 w-8 text-green-500 mx-auto mb-4" />
                      <div className="text-3xl font-bold text-gray-900 mb-2">50K+</div>
                      <div className="text-gray-600">CO₂ Reduction</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg backdrop-blur-lg bg-opacity-90 transform hover:scale-105 transition-transform duration-300">
                      <LineChart className="h-8 w-8 text-blue-500 mx-auto mb-4" />
                      <div className="text-3xl font-bold text-gray-900 mb-2">15%</div>
                      <div className="text-gray-600">ROI Average</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg backdrop-blur-lg bg-opacity-90 transform hover:scale-105 transition-transform duration-300">
                      <Globe2 className="h-8 w-8 text-purple-500 mx-auto mb-4" />
                      <div className="text-3xl font-bold text-gray-900 mb-2">1000+</div>
                      <div className="text-gray-600">Projects Done</div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <button
                      onClick={() => window.location.href = '/new-project'}
                      className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 px-8 rounded-lg text-lg font-medium hover:from-blue-700 hover:to-green-700 transform transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg"
                    >
                      Start Your Energy Journey
                    </button>
                    <DashboardButton
                      className="bg-white border-2 border-blue-600 text-blue-600 py-4 px-8 rounded-lg text-lg font-medium hover:bg-blue-50 transform transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg flex items-center justify-center"
                      setShowLoginModal={setShowLoginModal}
                    >
                      <LineChart className="h-5 w-5 mr-2" />
                      {user ? 'Dashboard' : 'Login'}
                    </DashboardButton>
                  </div>
                </div>
              </div>
            </div>

            {/* Energy Solutions Section */}
            <div className="py-20 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">Comprehensive Energy Solutions</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Our AI-powered platform designs custom energy systems to meet your specific needs and maximize efficiency.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 transform hover:scale-105 transition-duration-300">
                    <Sun className="h-12 w-12 text-yellow-500 mb-6" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Solar Solutions</h3>
                    <p className="text-gray-600 mb-6">
                      Harness the power of the sun with our advanced solar panel systems, designed for maximum efficiency and durability in any environment.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">High-efficiency panels</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Custom mounting solutions</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">25-year performance warranty</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 transform hover:scale-105 transition-duration-300">
                    <Battery className="h-12 w-12 text-green-500 mb-6" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Battery Storage</h3>
                    <p className="text-gray-600 mb-6">
                      Store excess energy and ensure continuous power with our cutting-edge battery storage solutions, designed for reliability and longevity.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Lithium-ion technology</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Scalable capacity</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Smart energy management</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 transform hover:scale-105 transition-duration-300">
                    <Zap className="h-12 w-12 text-blue-500 mb-6" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Hybrid Systems</h3>
                    <p className="text-gray-600 mb-6">
                      Get the best of both worlds with our hybrid energy systems, combining solar power with battery storage for optimal energy independence.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Seamless integration</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Intelligent power switching</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Grid-tie capability</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us Section */}
            <div className="py-20 bg-gradient-to-b from-white to-blue-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose ENTEK.AI</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Our AI-powered platform offers unmatched precision and efficiency in energy system design.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                    <div className="bg-blue-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
                      <Award className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Design</h3>
                    <p className="text-gray-600">
                      Our advanced algorithms create optimized energy system designs based on your specific needs and location.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                    <div className="bg-green-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
                      <LineChart className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Detailed Analytics</h3>
                    <p className="text-gray-600">
                      Get comprehensive insights into energy production, consumption, and savings with our detailed reporting.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                    <div className="bg-yellow-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
                      <Leaf className="h-8 w-8 text-yellow-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Sustainable Focus</h3>
                    <p className="text-gray-600">
                      Our solutions prioritize environmental sustainability while maximizing your energy independence.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                    <div className="bg-purple-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
                      <Shield className="h-8 w-8 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Support</h3>
                    <p className="text-gray-600">
                      Our team of energy experts is available to guide you through every step of your project.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Plans */}
            <div className="py-20 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">Subscription Plans</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Choose the plan that best fits your energy design needs.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 relative overflow-hidden flex flex-col h-full">
                    <div className="absolute top-0 right-0 bg-blue-100 text-blue-800 px-4 py-1 text-sm font-bold">
                      FREE
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic</h3>
                    <div className="text-3xl font-bold text-gray-900 mb-4">
                      SAR 0<span className="text-sm text-gray-500">/mo</span>
                    </div>
                    <ul className="space-y-3 mb-8 flex-grow">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">1 project</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Up to 20kW system size</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Basic support</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Standard features</span>
                      </li>
                    </ul>
                    {isLoading ? (
                      <button 
                        disabled
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium opacity-75 cursor-not-allowed flex items-center justify-center"
                      >
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </button>
                    ) : (
                      <DashboardButton
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
                        setShowLoginModal={setShowLoginModal}
                      >
                        {user ? 'Go to Dashboard' : 'Get Started'}
                      </DashboardButton>
                    )}
                  </div>

                  <div className="bg-white rounded-xl p-8 border-2 border-blue-500 relative overflow-hidden transform scale-105 shadow-xl flex flex-col h-full">
                    <div className="absolute top-0 right-0 bg-blue-500 text-white px-4 py-1 text-sm font-bold">
                      POPULAR
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Business</h3>
                    <div className="text-3xl font-bold text-gray-900 mb-4">
                      SAR 750<span className="text-sm text-gray-500">/mo</span>
                    </div>
                    <ul className="space-y-3 mb-8 flex-grow">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">5 projects</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Up to 50kW system size</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Priority support</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Advanced reporting</span>
                      </li>
                    </ul>
                    {isLoading ? (
                      <button 
                        disabled
                        className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 rounded-lg font-medium opacity-75 cursor-not-allowed flex items-center justify-center"
                      >
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </button>
                    ) : user ? (
                      <button 
                        onClick={() => window.location.href = '/subscription'}
                        className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white py-3 rounded-lg font-medium transition-colors"
                      >
                        Upgrade Now
                      </button>
                    ) : (
                      <button 
                        onClick={() => setShowLoginModal(true)}
                        className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white py-3 rounded-lg font-medium transition-colors"
                      >
                        Upgrade Now
                      </button>
                    )}
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 relative overflow-hidden flex flex-col h-full">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
                    <div className="text-3xl font-bold text-gray-900 mb-4">
                      SAR 1,500<span className="text-sm text-gray-500">/mo</span>
                    </div>
                    <ul className="space-y-3 mb-8 flex-grow">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">10 projects</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Up to 250kW system size</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Dedicated support</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Custom branding</span>
                      </li>
                    </ul>
                    {isLoading ? (
                      <button 
                        disabled
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium opacity-75 cursor-not-allowed flex items-center justify-center"
                      >
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </button>
                    ) : user ? (
                      <button 
                        onClick={() => window.location.href = '/subscription'}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
                      >
                        Contact Sales
                      </button>
                    ) : (
                      <button 
                        onClick={() => setShowLoginModal(true)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
                      >
                        Contact Sales
                      </button>
                    )}
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 relative overflow-hidden flex flex-col h-full">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Ultimate</h3>
                    <div className="text-3xl font-bold text-gray-900 mb-4">
                      SAR 2,500<span className="text-sm text-gray-500">/mo</span>
                    </div>
                    <ul className="space-y-3 mb-8 flex-grow">
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Unlimited projects</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Up to 2000kW system size</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">White glove support</span>
                      </li>
                      <li className="flex items-start">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">Custom integrations</span>
                      </li>
                    </ul>
                    {isLoading ? (
                      <button 
                        disabled
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium opacity-75 cursor-not-allowed flex items-center justify-center"
                      >
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </button>
                    ) : user ? (
                      <button 
                        onClick={() => window.location.href = '/subscription'}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
                      >
                        Contact Sales
                      </button>
                    ) : (
                      <button 
                        onClick={() => setShowLoginModal(true)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
                      >
                        Contact Sales
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonials Section */}
            <div className="py-20 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">What Our Clients Say</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Hear from businesses and homeowners who have transformed their energy consumption with ENTEK.AI.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 flex flex-col h-full">
                    <div className="flex items-center mb-6">
                      <img 
                        src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&h=100&q=80" 
                        alt="Client" 
                        className="w-14 h-14 rounded-full object-cover mr-4"
                      />
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">Ahmed Al-Farsi</h4>
                        <p className="text-gray-500">Residential Client</p>
                      </div>
                    </div>
                    <p className="text-gray-600 italic flex-grow">
                      "ENTEK.AI designed a solar system that reduced my energy bills by 90%. The AI-powered design process was incredibly thorough and the installation was flawless."
                    </p>
                    <div className="flex mt-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Sun key={star} className="h-5 w-5 text-yellow-500" />
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 flex flex-col h-full">
                    <div className="flex items-center mb-6">
                      <img 
                        src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=100&h=100&q=80" 
                        alt="Client" 
                        className="w-14 h-14 rounded-full object-cover mr-4"
                      />
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">Sarah Johnson</h4>
                        <p className="text-gray-500">Business Owner</p>
                      </div>
                    </div>
                    <p className="text-gray-600 italic flex-grow">
                      "The hybrid system ENTEK.AI designed for our manufacturing facility has been a game-changer. We've seen a 75% reduction in energy costs and improved our sustainability metrics."
                    </p>
                    <div className="flex mt-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Sun key={star} className="h-5 w-5 text-yellow-500" />
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 flex flex-col h-full">
                    <div className="flex items-center mb-6">
                      <img 
                        src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=100&h=100&q=80" 
                        alt="Client" 
                        className="w-14 h-14 rounded-full object-cover mr-4"
                      />
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">Michael Chen</h4>
                        <p className="text-gray-500">Corporate Client</p>
                      </div>
                    </div>
                    <p className="text-gray-600 italic flex-grow">
                      "The battery storage solution from ENTEK.AI has eliminated our power outage concerns. Their AI optimization ensured we got exactly the capacity we needed."
                    </p>
                    <div className="flex mt-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Sun key={star} className="h-5 w-5 text-yellow-500" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="py-20 bg-gradient-to-b from-white to-blue-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Transform Your Energy Future?</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                  Join thousands of satisfied customers who have made the switch to intelligent energy solutions.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <button
                    onClick={() => window.location.href = '/new-project'}
                    className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 px-8 rounded-lg text-lg font-medium hover:from-blue-700 hover:to-green-700 transform transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg"
                  >
                    Start Your Project
                  </button>
                  {isLoading ? (
                    <button
                      disabled
                      className="bg-transparent border-2 border-blue-500 text-blue-600 py-4 px-8 rounded-lg text-lg font-medium opacity-75 cursor-not-allowed flex items-center justify-center"
                    >
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Loading...
                    </button>
                  ) : (
                    <DashboardButton
                      className="bg-transparent border-2 border-blue-500 text-blue-600 py-4 px-8 rounded-lg text-lg font-medium hover:bg-blue-50 transform transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg flex items-center justify-center"
                      setShowLoginModal={setShowLoginModal}
                    >
                      <LineChart className="h-5 w-5 mr-2" />
                      {user ? 'Dashboard' : 'Login'}
                    </DashboardButton>
                  )}
                </div>
              </div>
            </div>

            <Footer />
            
            {/* Login Modal */}
            {showLoginModal && (
              <LoginModal 
                onClose={() => setShowLoginModal(false)}
                onLogin={() => {}}
                error={null}
                onStartNewProject={() => window.location.href = '/new-project'}
              />
            )}
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
